import React, { useState, useEffect } from 'react';
import { getDailySessions } from '../api/sessions';
import { Box, Typography, Stack, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Cookies from 'js-cookie';

const ReportViewer = ({ selectedDate }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertState, setAlertState] = useState(null);

  // Normalize the date to local midnight
  const currentDate = selectedDate instanceof Date ? selectedDate : new Date(selectedDate || Date.now());
  const localDate = new Date(currentDate.setHours(0, 0, 0, 0)); // Reset to local midnight
  const dateKey = localDate.toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' }); // YYYY-MM-DD in Berlin timezone

  const [shiftActive, setShiftActive] = useState(false);

  useEffect(() => {
    const savedShiftState = Cookies.get('shiftActive');
    setShiftActive(savedShiftState === 'true');

    const fetchReportData = async () => {
      try {
        setLoading(true);
        const data = await getDailySessions(dateKey);
        console.log('Fetched report data for', dateKey, ':', data);
        if (data && data.length > 0) {
          setReportData(data);
        } else {
          setReportData([]);
          setAlertState({ message: `No sessions found for ${dateKey}.`, severity: 'info' });
          setTimeout(() => setAlertState(null), 5000);
        }
      } catch (error) {
        console.error('Error fetching report data:', error.message);
        setReportData([]);
        setAlertState({ message: 'Failed to load report.', severity: 'error' });
        setTimeout(() => setAlertState(null), 5000);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [dateKey]);

  if (loading) {
    return <Typography>Loading report...</Typography>;
  }

  if (!reportData || reportData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">
          {selectedDate
            ? `No sessions found for ${new Date(selectedDate).toLocaleDateString()}.`
            : 'No report data found.'}
        </Typography>
      </Box>
    );
  }

  const columns = [
    { field: 'barber_name', headerName: 'Barber', flex: 2 },
    { field: 'services', headerName: 'Service', flex: 2 },
    { field: 'extras', headerName: 'Extras', flex: 2 },
    { field: 'items', headerName: 'Items', flex: 2 },
    { field: 'tips', headerName: 'Tips (€)', flex: 1 },
    { field: 'session', headerName: 'Session(€)', flex: 1 },
    { field: 'summed_price', headerName: 'Total(€)', flex: 1 },
  ];

  const rows = reportData.map((item, index) => ({
    id: index,
    barber_name: item.barber_name,
    services: item.services && Array.isArray(item.services)
      ? item.services.map(service => service.name).join(', ')
      : 'None',
    extras: item.extras && Array.isArray(item.extras)
      ? item.extras.map(extra => extra.name).join(', ')
      : 'None',
    items: item.items && Array.isArray(item.items)
      ? item.items.map(itemObj => itemObj.name).join(', ')
      : 'None',
    tips: item.tip && typeof item.tip === 'number' && !isNaN(item.tip)
      ? item.tip.toFixed(2)
      : '0.00',
    session: item.total_price && typeof item.total_price === 'number' && !isNaN(item.total_price)
      ? item.total_price.toFixed(2)
      : 'N/A',
    // Add together the total_price and the tip as numbers, then format the result
    summed_price: (
      (item.total_price && typeof item.total_price === 'number' && !isNaN(item.total_price) 
        ? item.total_price 
        : 0) + 
      (item.tip && typeof item.tip === 'number' && !isNaN(item.tip) 
        ? item.tip 
        : 0)
    ).toFixed(2),
  }));

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Kassenbericht", 15, 20); // Title translated to "Cash Report"
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Datum: " + currentDate.toLocaleDateString(), 15, 30); // "Date" translated to "Datum"
    doc.text("Tijd: " + currentDate.toLocaleTimeString(), 15, 38); // "Uhrzeit" translated to "Tijd"
    doc.setFontSize(10);
    doc.setLineWidth(0.5);
    doc.line(15, 42, 195, 42); // Line adjusted to be below date and time


    const tableHeaders = [
      'Service', 'Extras', 'Items', 'Tips (€)', 'Session (€)', 'Total (€)'
    ];

    // Map rows correctly to match the headers
    const tableRows = rows.map(row => [
      row.services,
      row.extras,
      row.items,
      row.tips,
      row.session,
      row.summed_price, // Use summed_price for the total column
    ]);

    autoTable(doc, {
      startY: 45,
      head: [tableHeaders],
      body: tableRows,
      theme: 'striped',
      headStyles: {
        fillColor: [255, 102, 102],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
    });

    // Calculate totals for the summary table
    const totalPrice = rows.reduce((acc, row) => acc + parseFloat(row.session || 0), 0).toFixed(2);
    const totalTips = rows.reduce((acc, row) => acc + parseFloat(row.tips || 0), 0).toFixed(2);
    const summedPrice = rows.reduce((acc, row) => acc + (parseFloat(row.session) || 0) + (parseFloat(row.tips) || 0), 0).toFixed(2);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Item', 'Total (€)']],
      body: [
        ['Total Session', totalPrice],
        ['Total Tips', totalTips],
        ['Summed Money', summedPrice],
      ],
      theme: 'striped',
      headStyles: {
        fillColor: [255, 102, 102],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
    });

    const yPosition = doc.lastAutoTable.finalY + 20;

    // Add the stamp image
    const stampImage = `${process.env.PUBLIC_URL}/stamp.png`; // Path to the image in the public folder
    const imgWidth = 50; // Adjust the width of the image
    const imgHeight = 30; // Adjust the height of the image
    doc.addImage(stampImage, 'PNG', 120, yPosition, imgWidth, imgHeight);

    // Add the signature line
    doc.text("__________________", 20, yPosition + 10);
    doc.text("Unterschrift", 30, yPosition + 15);

    doc.save(`Kassenbericht-${dateKey}.pdf`);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Kassenbericht - {localDate.toLocaleDateString()} {/* Use localDate here */}
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleExportPDF}
          disabled={shiftActive}
        >
          {shiftActive ? 'Schließe die Schicht, um zu exportieren' : 'Export as PDF'}
        </Button>
      </Stack>

      <div style={{ height: 800, width: '100%', marginTop: '20px' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          disableColumnResize
          density="compact"
        />
      </div>
    </Box>
  );
};

export default ReportViewer;